import type {
  ApiErrorDto,
  MeResponseDto,
  OrgBrandingDto,
  OrgMembersResponseDto,
  OrgSubscriptionsResponseDto,
  PlansResponseDto,
} from '@shared/api-dto';
import { ApiError } from './errors';

/** What POST /auth/refresh and /auth/magic/consume return (the `sendAuth` shape). */
export interface AuthStateDto {
  accessToken: string | null;
  expiresIn: number | null;
  needsOrg: boolean;
  needsSelection: boolean;
  selectedOrgId: string | null;
  orgs: { id: string; name: string; slug: string; rootRole: string }[];
}

/** The X-RateLimit-* snapshot from a gated product call, normalized for the UI. */
export interface QuotaSnapshot {
  limit: number;
  remaining: number;
  window: 'min' | 'day' | 'mon';
  /** ISO string. The header is UNIX SECONDS — a raw `new Date(1751328000)` is 1970, not 2026. */
  resetAt: string | null;
}

function readQuota(res: Response): QuotaSnapshot | null {
  const limit = res.headers.get('X-RateLimit-Limit');
  const remaining = res.headers.get('X-RateLimit-Remaining');
  const window = res.headers.get('X-RateLimit-Window');
  // A route with no quota configured sends no headers at all — that's "no meter", not "zero left".
  if (limit === null || remaining === null || window === null) return null;

  const resetSeconds = Number(res.headers.get('X-RateLimit-Reset'));
  return {
    limit: Number(limit),
    remaining: Number(remaining),
    window: window as QuotaSnapshot['window'],
    resetAt: Number.isFinite(resetSeconds) && resetSeconds > 0 ? new Date(resetSeconds * 1000).toISOString() : null,
  };
}

export interface HuacaClientOptions {
  /** API origin, e.g. https://staging-api.huaca.io */
  apiBase: string;
  /** This module's own origin — where logins must return to. */
  webOrigin?: string;
  onAuthStateChange?: (state: AuthStateDto | null) => void;
}

/** Fraction of the token's life after which we proactively refresh (keeps 401s off the hot path). */
const REFRESH_AT = 0.8;

export class HuacaClient {
  private readonly apiBase: string;
  private readonly webOrigin: string;
  private readonly onAuthStateChange: ((s: AuthStateDto | null) => void) | undefined;

  /** In memory ONLY — never localStorage. An XSS that can read the token can impersonate the user
   *  for its whole lifetime; keeping it off disk matches the backend's HttpOnly-refresh design. */
  private accessToken: string | null = null;
  private expiresAtMs = 0;

  /**
   * The single in-flight refresh. This is NOT an optimisation — it is required for correctness.
   * rotateSession() revokes the ENTIRE token family if a consumed refresh token is replayed outside
   * a 10s grace window, which logs the user out of every device. N concurrent 401s each firing their
   * own /auth/refresh is exactly that scenario. One shared promise; everyone awaits it.
   */
  private refreshInFlight: Promise<AuthStateDto> | null = null;

  constructor(opts: HuacaClientOptions) {
    this.apiBase = opts.apiBase.replace(/\/$/, '');
    this.webOrigin = opts.webOrigin ?? (typeof location !== 'undefined' ? location.origin : '');
    this.onAuthStateChange = opts.onAuthStateChange;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private setAuthState(state: AuthStateDto): AuthStateDto {
    this.accessToken = state.accessToken;
    // `expiresIn` is null whenever accessToken is null (needsOrg / needsSelection).
    this.expiresAtMs = state.accessToken && state.expiresIn ? Date.now() + state.expiresIn * 1000 : 0;
    this.onAuthStateChange?.(state);
    return state;
  }

  clearAuth(): void {
    this.accessToken = null;
    this.expiresAtMs = 0;
    this.onAuthStateChange?.(null);
  }

  private async parse<T>(res: Response): Promise<T> {
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    const body: unknown = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const err = (body as ApiErrorDto).error;
      throw new ApiError(
        res.status,
        err ?? { code: 'INTERNAL', message: `HTTP ${res.status}` },
      );
    }
    return body as T;
  }

  /**
   * The single fetch every call funnels through, parameterised by the credential it may carry.
   *
   * `credentials` is a REQUIRED argument rather than a defaulted one. It is the security-relevant
   * half of each call, and a default is precisely how a new caller inherits one without deciding.
   */
  private async send<T>(path: string, init: RequestInit, credentials: RequestCredentials): Promise<T> {
    const res = await fetch(`${this.apiBase}${path}`, {
      ...init,
      credentials,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
    return this.parse<T>(res);
  }

  /** Raw call — no auth, no retry. Used by the public/cookie endpoints. */
  private async raw<T>(path: string, init: RequestInit = {}): Promise<T> {
    // The refresh cookie is HttpOnly + Domain=.huaca.io; it only travels with credentials.
    return this.send<T>(path, init, 'include');
  }

  // ── Session ────────────────────────────────────────────────────────────────
  /**
   * Exchange the refresh cookie for an access token. Always single-flight: concurrent callers get
   * the SAME promise, so the server only ever sees one rotation.
   */
  async refresh(): Promise<AuthStateDto> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this.raw<AuthStateDto>('/auth/refresh', { method: 'POST', body: '{}' })
      .then((s) => this.setAuthState(s))
      .finally(() => {
        this.refreshInFlight = null;
      });
    return this.refreshInFlight;
  }

  /** A valid token, refreshing proactively at ~80% of its life so 401s stay off the hot path. */
  private async ensureToken(): Promise<string | null> {
    if (this.accessToken && Date.now() < this.expiresAtMs) return this.accessToken;
    const state = await this.refresh();
    // NOTE: a 200 does NOT guarantee a token — it's null when needsOrg/needsSelection. Callers must
    // route to onboarding rather than retrying, or they'd spin forever.
    return state.accessToken;
  }

  /** Authenticated call: Bearer token, one refresh-and-retry on 401, never a loop. */
  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const token = await this.ensureToken();
    const call = (bearer: string | null): Promise<Response> =>
      fetch(`${this.apiBase}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          ...(init.headers ?? {}),
        },
      });

    let res = await call(token);
    if (res.status === 401) {
      // Exactly one retry. The refresh is single-flight, so a burst of 401s still rotates once.
      const state = await this.refresh();
      if (!state.accessToken) return this.parse<T>(res); // no token to be had → surface the 401
      res = await call(state.accessToken);
    }
    return this.parse<T>(res);
  }

  /**
   * Unauthenticated call to a route the API serves publicly. No token, no cookie, no retry.
   *
   * NOT `request()`, AND THE DIFFERENCE IS NOT AN OPTIMISATION. `request()` awaits `ensureToken()`
   * first; for a visitor with no refresh cookie that performs `POST /auth/refresh`, which the API
   * answers 401 (`if (!raw) throw new UnauthorizedError('No session')`), so `request()` REJECTS
   * before it ever fetches the path it was given. A public route asked for that way is never
   * requested at all — the failure is total, and from the network panel it is invisible, because
   * the only call on the wire is the refresh. That is not hypothetical: it is how the tenders
   * landing page came to render no sources while issuing no request anyone could find.
   *
   * `credentials: 'omit'` — the deliberate difference from `raw()`, which sends 'include'. Two
   * reasons, both about the credential rather than the response:
   *   - Least privilege. A genuinely public route has no use for a session cookie, and a route that
   *     receives one is on the CSRF surface the day it grows a side effect. "It was read-only when
   *     we wrote it" is how that happens.
   *   - These responses are served `Cache-Control: public, max-age=300`. A credentialed request
   *     against a shared-cacheable response is the SHAPE of a cross-user caching bug even when the
   *     body is identical for every visitor, and it should not have to be re-audited each time
   *     someone notices the pairing.
   *
   * Throws `ApiError` on a non-2xx exactly as `request()` does, so a caller that must survive the
   * failure — a login screen, say — still has to catch it.
   */
  async publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.send<T>(path, init, 'omit');
  }

  /**
   * Like `request`, but also returns the quota snapshot from the X-RateLimit-* headers.
   *
   * Separate from `request` because those headers only exist on gated product routes, and because
   * they are readable cross-origin ONLY thanks to the API's `exposedHeaders` — if they ever come
   * back null on a gated route, suspect that config before suspecting this code.
   */
  async requestWithQuota<T>(path: string, init: RequestInit = {}): Promise<{ data: T; quota: QuotaSnapshot | null }> {
    const token = await this.ensureToken();
    const call = (bearer: string | null): Promise<Response> =>
      fetch(`${this.apiBase}${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
          ...(init.headers ?? {}),
        },
      });

    let res = await call(token);
    if (res.status === 401) {
      const state = await this.refresh();
      if (state.accessToken) res = await call(state.accessToken);
    }
    // Read the headers BEFORE parse(), which throws on a non-2xx — a 429 carries the most
    // interesting quota snapshot of all, and throwing first would discard it.
    const quota = readQuota(res);
    const data = await this.parse<T>(res);
    return { data, quota };
  }

  // ── Auth ───────────────────────────────────────────────────────────────────
  async requestMagicLink(email: string): Promise<{ ok: true; _devToken?: string }> {
    return this.raw('/auth/magic/request', {
      method: 'POST',
      body: JSON.stringify({ email, origin: this.webOrigin }),
    });
  }

  async consumeMagicLink(token: string): Promise<AuthStateDto> {
    const state = await this.raw<AuthStateDto>('/auth/magic/consume', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
    return this.setAuthState(state);
  }

  /** Full-page navigation — the OAuth handshake can't be done over fetch. */
  googleLoginUrl(): string {
    return `${this.apiBase}/auth/google/start?origin=${encodeURIComponent(this.webOrigin)}`;
  }

  async selectOrg(orgId: string): Promise<{ accessToken: string; expiresIn: number }> {
    const minted = await this.raw<{ accessToken: string; expiresIn: number }>('/auth/token', {
      method: 'POST',
      body: JSON.stringify({ orgId }),
    });
    this.accessToken = minted.accessToken;
    this.expiresAtMs = Date.now() + minted.expiresIn * 1000;
    return minted;
  }

  async logout(): Promise<void> {
    await this.raw('/auth/logout', { method: 'POST', body: '{}' });
    this.clearAuth();
  }

  // ── Reads ──────────────────────────────────────────────────────────────────
  me(): Promise<MeResponseDto> {
    return this.request<MeResponseDto>('/auth/me');
  }

  updateName(name: string | null): Promise<MeResponseDto> {
    return this.request<MeResponseDto>('/auth/me', { method: 'PATCH', body: JSON.stringify({ name }) });
  }

  /** Public — no session needed. */
  plans(productId: string): Promise<PlansResponseDto> {
    return this.raw<PlansResponseDto>(`/plans?productId=${encodeURIComponent(productId)}`, { method: 'GET' });
  }

  // ── Billing ────────────────────────────────────────────────────────────────
  startTrial(planId: string) {
    return this.request<{ subscriptionId: string; planId: string; endsAt: string | null }>('/billing/trial', {
      method: 'POST',
      body: JSON.stringify({ planId }),
    });
  }

  checkout(planId: string, redirectUrl?: string) {
    return this.request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ planId, ...(redirectUrl ? { redirectUrl } : {}) }),
    });
  }

  // ── Org admin ──────────────────────────────────────────────────────────────
  orgMembers() {
    return this.request<OrgMembersResponseDto>('/org/members');
  }

  orgSubscriptions() {
    return this.request<OrgSubscriptionsResponseDto>('/org/subscriptions');
  }

  /**
   * Invite by email. `seatSubscriptionId` + `moduleRoles` are PROMISES granted at their first login
   * — the difference between the invitee landing in the product and landing on "ask for a seat".
   */
  inviteMember(
    email: string,
    rootRole: 'owner' | 'admin' | 'member' = 'member',
    opts: { seatSubscriptionId?: string; moduleRoles?: Record<string, string[]> } = {},
  ) {
    return this.request<{ ok: true; email: string; _devToken?: string }>('/org/members/invite', {
      method: 'POST',
      body: JSON.stringify({
        email,
        rootRole,
        origin: this.webOrigin,
        ...(opts.seatSubscriptionId ? { seatSubscriptionId: opts.seatSubscriptionId } : {}),
        ...(opts.moduleRoles ? { moduleRoles: opts.moduleRoles } : {}),
      }),
    });
  }

  /** Revoke a pending invite — also releases the seat it was holding against capacity. */
  revokeInvite(email: string) {
    return this.request(`/org/members/invite/${encodeURIComponent(email)}`, { method: 'DELETE' });
  }

  assignSeat(subscriptionId: string, userId: string) {
    return this.request(`/org/subscriptions/${subscriptionId}/seats`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  releaseSeat(subscriptionId: string, userId: string) {
    return this.request(`/org/subscriptions/${subscriptionId}/seats/${userId}`, { method: 'DELETE' });
  }

  setModuleRoles(userId: string, productId: string, roleIds: string[]) {
    return this.request(`/org/members/${userId}/module-roles`, {
      method: 'POST',
      body: JSON.stringify({ productId, roleIds }),
    });
  }

  // ── Org branding (firm logo) ─────────────────────────────────────────────────
  /** The org's current logo metadata + a fresh signed preview URL (nulls if none). */
  getOrgBranding(): Promise<OrgBrandingDto> {
    return this.request<OrgBrandingDto>('/org/branding');
  }

  /** Step 1 of the logo upload: a signed PUT URL + the storage path to confirm. */
  orgLogoUploadUrl(body: { filename: string; contentType: string; sizeBytes: number }): Promise<{ uploadUrl: string; storagePath: string }> {
    return this.request('/org/branding/logo/upload-url', { method: 'POST', body: JSON.stringify(body) });
  }

  /** Step 3: record the logo after the object has landed; returns the new branding state. */
  confirmOrgLogo(body: { storagePath: string; filename: string }): Promise<OrgBrandingDto> {
    return this.request<OrgBrandingDto>('/org/branding/logo/confirm', { method: 'POST', body: JSON.stringify(body) });
  }

  /** Remove the org logo (idempotent). */
  deleteOrgLogo(): Promise<void> {
    return this.request('/org/branding/logo', { method: 'DELETE' }).then(() => undefined);
  }
}

export { REFRESH_AT };
